import {
  BpmByNDCAndJobType,
  BpmByNDCAndJobTypePayload,
  FgNdc
} from 'Interfaces/ReferenceInterfaces/StandardAndRecipeBpmInterface';
import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { bpmByNdcAndJobTypeServices } from 'services/ReferenceScreenServices/bpmByNdcAndJobTypeServices';

export class BpmByNdcAndJobTypeStore {
  bpmByNdcAndJobTypes: BpmByNDCAndJobType[] = [];
  isLoading: boolean = false;
  isPolling: boolean = false;
  pagination = {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrevious: false
  };
  selectedBpmByNdcAndJobType: BpmByNDCAndJobType | null = null;
  searchQuery: string = '';
  fgNDC: FgNdc[] = [];
  environment = import.meta.env.VITE_ENVIRONMENT;

  constructor() {
    makeAutoObservable(this);
  }

  // Initial load method (with loading indicator)
  fetchBpmByNdcAndJobTypes = async (page = 1, pageSize = 10000, search?: string) => {
    this.isLoading = true;
    try {
      const response = await bpmByNdcAndJobTypeServices.getAllBpmByNdcAndJobTypes(
        page,
        pageSize,
        search
      );
      runInAction(() => {
        this.bpmByNdcAndJobTypes = response.data;
        this.pagination = {
          currentPage: response.pagination.current_page,
          totalPages: response.pagination.total_pages,
          totalItems: response.pagination.total_items,
          hasNext: response.pagination.has_next,
          hasPrevious: response.pagination.has_previous
        };
        if (search !== undefined) {
          this.searchQuery = search;
        }
      });
    } catch (error) {
      toast.error('Error fetching BPM by NDC and job types');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  fetchAllFgNDC = async () => {
    try {
      const response = await bpmByNdcAndJobTypeServices.getAllFgNDC();
      runInAction(() => {
        this.fgNDC = response.data;
      });
    } catch (error) {
      if (this.environment === 'development') {
        console.error('Error fetching all FG NDC:', error);
      }
      toast.error('Error fetching all FG NDC!');
    }
  };

  // Polling method (without loading indicator)
  pollBpmByNdcAndJobTypes = async (page = 1, pageSize = 10000, search?: string) => {
    this.isPolling = true;
    try {
      const response = await bpmByNdcAndJobTypeServices.getAllBpmByNdcAndJobTypes(
        page,
        pageSize,
        search
      );
      runInAction(() => {
        // Update data only if it has changed
        if (JSON.stringify(this.bpmByNdcAndJobTypes) !== JSON.stringify(response.data)) {
          this.bpmByNdcAndJobTypes = response.data;
          this.pagination = {
            currentPage: response.pagination.current_page,
            totalPages: response.pagination.total_pages,
            totalItems: response.pagination.total_items,
            hasNext: response.pagination.has_next,
            hasPrevious: response.pagination.has_previous
          };
          if (search !== undefined) {
            this.searchQuery = search;
          }
        }
      });
    } catch (error) {
      // Don't show toast for polling errors to avoid spam
      if (this.environment === 'development') {
        console.error('Error polling BPM by NDC and job types:', error);
      }
      toast.error('Error polling BPM by NDC and job types!');
    } finally {
      runInAction(() => {
        this.isPolling = false;
      });
    }
  };

  // Method to refresh data (can be called manually)
  refreshData = async (page = 1, pageSize = 10000, search?: string) => {
    await this.fetchBpmByNdcAndJobTypes(page, pageSize, search);
  };

  createBpmByNdcAndJobType = async (
    bpmByNdcAndJobTypeData: BpmByNDCAndJobTypePayload
  ): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response =
        await bpmByNdcAndJobTypeServices.createBpmByNdcAndJobType(bpmByNdcAndJobTypeData);
      if (response.status) {
        toast.success('BPM by NDC and job type created successfully');
        await this.fetchBpmByNdcAndJobTypes(this.pagination.currentPage);
        return true;
      } else {
        toast.error(response.message || 'Failed to create BPM by NDC and job type');
        return false;
      }
    } catch (error) {
      toast.error('Error creating BPM by NDC and job type');
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  updateBpmByNdcAndJobType = async (
    id: number,
    bpmByNdcAndJobTypeData: BpmByNDCAndJobTypePayload
  ): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response = await bpmByNdcAndJobTypeServices.updateBpmByNdcAndJobType(
        id,
        bpmByNdcAndJobTypeData
      );
      if (response.status) {
        toast.success('BPM by NDC and job type updated successfully');
        await this.fetchBpmByNdcAndJobTypes(this.pagination.currentPage);
        return true;
      } else {
        toast.error(response.message || 'Failed to update BPM by NDC and job type');
        return false;
      }
    } catch (error) {
      toast.error('Error updating BPM by NDC and job type');
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  deleteBpmByNdcAndJobType = async (id: number): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response = await bpmByNdcAndJobTypeServices.deleteBpmByNdcAndJobType(id);
      if (response.status) {
        toast.success('BPM by NDC and job type deleted successfully');
        await this.fetchBpmByNdcAndJobTypes(this.pagination.currentPage);
        return true;
      } else {
        toast.error(response.message || 'Failed to delete BPM by NDC and job type');
        return false;
      }
    } catch (error) {
      toast.error('Error deleting BPM by NDC and job type');
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // Method to select a BPM by NDC and job type
  selectBpmByNdcAndJobType = (bpmByNdcAndJobType: BpmByNDCAndJobType | null) => {
    this.selectedBpmByNdcAndJobType = bpmByNdcAndJobType;
  };

  // Method to clear selection
  clearSelection = () => {
    this.selectedBpmByNdcAndJobType = null;
  };
}

// Create and export instance
export const bpmByNdcAndJobTypeStore = new BpmByNdcAndJobTypeStore();
